import { Image as ImageIcon } from "lucide-react";
import type { ProductImage } from "@sakura-tech-lab/dashboard-api";

import { CatalogueImage } from "@/components/catalog/catalogue-image";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface ProductMediaGalleryProps {
  readonly images: readonly ProductImage[];
  readonly productName: string;
  readonly selectedImageIndex: number;
  readonly onSelectImage: (index: number) => void;
}

export function ProductMediaGallery({
  images,
  productName,
  selectedImageIndex,
  onSelectImage,
}: ProductMediaGalleryProps) {
  const selectedImage = images[selectedImageIndex] ?? images[0];

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="p-3 sm:p-4">
        <div className="flex aspect-[4/3] max-h-[400px] min-h-[240px] min-w-0 items-center justify-center overflow-hidden rounded-md bg-muted/30">
          {selectedImage ? (
            <CatalogueImage
              key={selectedImage.id || selectedImage.url}
              image={selectedImage}
              width={960}
              alt={selectedImage.altText || productName}
              className="h-full w-full object-contain"
            />
          ) : (
            <div
              role="img"
              aria-label={`${productName} — no product images available`}
              className="flex h-full w-full flex-col items-center justify-center gap-3 text-muted-foreground"
            >
              <ImageIcon aria-hidden="true" className="h-10 w-10 opacity-40" />
              <p className="text-sm">No images available</p>
            </div>
          )}
        </div>

        {images.length > 1 ? (
          <div className="mt-3 flex max-w-full gap-2 overflow-x-auto pb-1" aria-label="Product images">
            {images.map((image, index) => (
              <Button
                key={image.id || image.url}
                type="button"
                variant="outline"
                className={cn(
                  "h-16 w-16 shrink-0 overflow-hidden rounded-md p-0",
                  selectedImageIndex === index && "border-primary ring-2 ring-ring/20",
                )}
                aria-label={`Show product image ${index + 1} of ${images.length}`}
                aria-pressed={selectedImageIndex === index}
                onClick={() => onSelectImage(index)}
              >
                <CatalogueImage
                  image={image}
                  width={128}
                  alt={image.altText || `${productName} thumbnail ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </Button>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
